import { useCallback, useMemo, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'html-formatter'
const PRESERVED_BLOCK_PATTERN = /<(pre|textarea|script|style)\b[\s\S]*?<\/\1>/gi

type HtmlResult = { ok: true; result: string } | { ok: false; error: string }

interface ParserLocation {
  start?: {
    line?: number
    column?: number
  }
}

interface ParserIssue extends Error {
  loc?: ParserLocation
}

async function loadHtmlFormatter() {
  const [prettier, htmlPlugin] = await Promise.all([
    import('prettier/standalone'),
    import('prettier/plugins/html'),
  ])

  return { prettier, htmlPlugin }
}

function formatHtmlIssue(error: unknown): string {
  if (!(error instanceof Error)) return String(error)

  const parserIssue = error as ParserIssue
  const location = parserIssue.loc?.start
  if (location?.line && location.column) {
    return `line ${location.line}, column ${location.column}: ${error.message}`
  }

  return error.message
}

async function formatHtml(input: string): Promise<HtmlResult> {
  if (!input.trim()) return { ok: false, error: 'empty' }

  try {
    const { prettier, htmlPlugin } = await loadHtmlFormatter()
    const result = await prettier.format(input, {
      parser: 'html',
      plugins: [htmlPlugin],
      htmlWhitespaceSensitivity: 'css',
      printWidth: 100,
      tabWidth: 2,
    })

    return { ok: true, result: result.trimEnd() }
  } catch (error) {
    return { ok: false, error: formatHtmlIssue(error) }
  }
}

function collapseHtmlWhitespace(input: string): string {
  const preservedBlocks: string[] = []
  const withPlaceholders = input.replace(PRESERVED_BLOCK_PATTERN, (match) => {
    const index = preservedBlocks.push(match) - 1
    return `___HTML_FORMATTER_PRESERVED_${index}___`
  })

  const collapsed = withPlaceholders
    .replace(/\s+/g, ' ')
    .replace(/>\s+</g, '><')
    .trim()

  return collapsed.replace(
    /___HTML_FORMATTER_PRESERVED_(\d+)___/g,
    (_, index: string) => preservedBlocks[Number(index)] ?? '',
  )
}

async function minifyHtml(input: string): Promise<HtmlResult> {
  const formatted = await formatHtml(input)
  if (!formatted.ok) return formatted

  return { ok: true, result: collapseHtmlWhitespace(formatted.result) }
}

export function HtmlFormatterPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [copied, setCopied] = useState(false)

  const inputStats = useMemo(() => {
    const chars = input.length
    const lines = input ? input.split('\n').length : 0
    return { chars, lines }
  }, [input])

  const outputStats = useMemo(() => {
    const chars = output.length
    const lines = output ? output.split('\n').length : 0
    return { chars, lines }
  }, [output])

  const processHtml = useCallback(async (action: 'format' | 'minify') => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      setStatusMessage(null)
      return
    }

    const start = performance.now()
    setIsProcessing(true)
    setError(null)
    setStatusMessage(null)
    toolInvoke(UTILITY, { action })

    const result = action === 'format'
      ? await formatHtml(input)
      : await minifyHtml(input)

    if (result.ok) {
      setOutput(result.result)
      setStatusMessage(
        action === 'format' ? t.htmlFormatter.formatted : t.htmlFormatter.minified,
      )
      toolSuccess(UTILITY, Math.round(performance.now() - start), { action })
    } else {
      setOutput('')
      setError(t.htmlFormatter.errors.invalidHtml.replace('{error}', result.error))
      toolError(UTILITY, 'invalid-html', { action })
    }

    setIsProcessing(false)
  }, [input, t])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleClear = useCallback(() => {
    setInput('')
    setOutput('')
    setError(null)
    setStatusMessage(null)
  }, [])

  return (
    <ToolPageShell toolKey="htmlFormatter" width="wide">
      <CodeWorkspace
        error={error}
        notice={statusMessage}
        controls={(
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => void processHtml('format')} disabled={!input.trim() || isProcessing}>
              {t.htmlFormatter.format}
            </Button>
            <Button
              variant="secondary"
              onClick={() => void processHtml('minify')}
              disabled={!input.trim() || isProcessing}
            >
              {t.htmlFormatter.minify}
            </Button>
          </div>
        )}
        input={{
          title: t.htmlFormatter.input,
          value: input,
          onChange: (value) => {
            setInput(value)
            setError(null)
            setStatusMessage(null)
          },
          placeholder: t.htmlFormatter.inputPlaceholder,
          ariaLabel: t.htmlFormatter.input,
          clear: { label: t.htmlFormatter.clear, onClick: handleClear },
          stats: [
            {
              label: t.htmlFormatter.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.htmlFormatter.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: t.htmlFormatter.output,
          value: output,
          readOnly: true,
          placeholder: t.htmlFormatter.outputPlaceholder,
          ariaLabel: t.htmlFormatter.output,
          actions: (
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? t.htmlFormatter.copied : t.htmlFormatter.copy}
            </Button>
          ),
          stats: output && !error
            ? [
              {
                label: t.htmlFormatter.characters,
                value: outputStats.chars.toLocaleString(localeCode),
              },
              {
                label: t.htmlFormatter.lines,
                value: outputStats.lines.toLocaleString(localeCode),
              },
            ]
            : undefined,
        }}
      />

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.htmlFormatter.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.htmlFormatter.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.htmlFormatter.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.htmlFormatter.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
